import React, { useEffect } from "react";
import { Controller, useForm, useFieldArray } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@apollo/client";
import { intl } from "LocaleWrapper";

import { Alert, Grid2 as Grid } from "@mui/material";

import { FormDialog } from "components/dialogs/Dialogs";
import DatePicker from "components/cck/DatePicker";
import { FormErrorMessage } from "components/form/FormErrorMessage";
import { FormArrayOnlyRows } from "components/cck/FormArray";

import LocationSelect from "view/locations/components/selects/LocationSelect";
import { rescheduledVoyageFormSchema, RescheduledVoyageFormSchemaInput, RescheduledVoyageFormSchemaOutput } from "view/voyages/utils/voyage.schema";

import { ChildProps } from "types/commons";
import { LocationType } from "__generated__/enums";
import { GeneralCallFragment } from "__generated__/view/calls/graphql/call.fragments.generated";
import { RescheduleVoyageDocument } from "__generated__/view/voyages/graphql/voyage.mutations.generated";

type RescheduledVoyageCallsEditProps = {
	open: boolean;
	voyage: Pick<ChildProps<GeneralCallFragment, "voyage">, "id" | "calls">
	onClose: () => void;
	refetch: () => void;
};

const VoyageRescheduleCallsDialog = ({ open, onClose, voyage, refetch }: RescheduledVoyageCallsEditProps) => {

	const { control, handleSubmit, reset } = useForm<RescheduledVoyageFormSchemaInput, unknown, RescheduledVoyageFormSchemaOutput>({
		resolver: zodResolver(rescheduledVoyageFormSchema),
		defaultValues: {
			calls: [],
		},
	});

	const { fields } = useFieldArray({
		control,
		name: "calls",
	});

	const [ rescheduleVoyageMutation, { loading } ] = useMutation(RescheduleVoyageDocument);

	const handleOnClose = () => {
		onClose();
	};

	const onRescheduleVoyage = (data: RescheduledVoyageFormSchemaOutput) => {
		rescheduleVoyageMutation({
			variables: {
				input: {
					id: voyage.id,
					calls: data.calls,
				},
			},
		}).then(() => {
			onClose();
			refetch();
		});
	};

	useEffect(() => {
		if (open) {
			reset({
				calls: voyage.calls.map(call => ({
					id: call.id,
					location: call.location,
					pta: call.pta ?? null,
					ptd: call.ptd ?? null,
				})) ?? [],
			});
		}
	}, [ open ]);

	const locationTypes = [
		LocationType.Terminal,
		LocationType.Area,
		LocationType.Port,
		LocationType.InlandTerminal,
		LocationType.Waterway,
		LocationType.Buoy,
		LocationType.ExtractionMachine,
	];

	return (
		<FormDialog
			open={open}
			onClose={handleOnClose}
			title={intl.formatMessage({ id: "RESCHEDULE_VOYAGE_CALLS" })}
			form="voyageRescheduleCallsForm"
			maxWidth="md"
			loading={loading}
			saveButtonDisabled={fields.length === 0}>
			<form onSubmit={handleSubmit(onRescheduleVoyage)} id="voyageRescheduleCallsForm">
				<Grid container spacing={3}>
					<Grid size={12}>

						{fields.length > 0 ? (
							<FormArrayOnlyRows
								array={fields}
								fields={[ {
									title: "",
									render: (index) => <div style={{ fontWeight: 600 }}>#{index + 1}</div>,
								}, {
									title: intl.formatMessage({ id: "LOCATION" }),
									required: true,
									render: (index) => (
										<Controller
											name={`calls.${index}.location`}
											control={control}
											render={({ field, formState }) => (
												<React.Fragment>
													<LocationSelect
														{...field}
														types={locationTypes}
														clearable={false}
													/>

													<FormErrorMessage name={field.name} errors={formState.errors} />
												</React.Fragment>
											)} />
									),
								}, {
									title: intl.formatMessage({ id: "PTA" }),
									required: true,
									render: (index) => (
										<Controller
											name={`calls.${index}.pta`}
											control={control}
											render={({ field, formState }) => (
												<React.Fragment>
													<DatePicker { ...field } showTimeInput />
													<FormErrorMessage name={field.name} errors={formState.errors} />
												</React.Fragment>
											)} />
									),
								}, {
									title: intl.formatMessage({ id: "PTD" }),
									required: true,
									render: (index) => (
										<Controller
											name={`calls.${index}.ptd`}
											control={control}
											render={({ field, formState }) => (
												<React.Fragment>
													<DatePicker { ...field } showTimeInput />
													<FormErrorMessage name={field.name} errors={formState.errors} />
												</React.Fragment>
											)} />
									),
								} ]} />
						) : (
							<Alert severity="info">
								{intl.formatMessage({ id: "AT_LEAST_ONE_CALL" })}
							</Alert>
						)}
					</Grid>
				</Grid>
			</form>
		</FormDialog>
	);
};

export default VoyageRescheduleCallsDialog;
